/**
 * Pure finance navigation contracts — no DOM, no React, importable from tests.
 *
 * Single source of truth for:
 *  - which roles can see the Finance section
 *  - Finance sub-items and their canonical capability gates
 *  - the localStorage key for persist state
 *  - the route-active predicates
 */

import { hasCapability, FinanceRole } from "@shared/finance/capabilities";
import type { FinanceCapability } from "@shared/finance/capabilities";

export type { FinanceRole };

export const FINANCE_EXPANDED_KEY = "finance-nav-expanded";

export type FinanceSubItem = Readonly<{
  label: string;
  path: string;
  requiredCapability: FinanceCapability;
  adminOnly?: boolean;
}>;

export const FINANCE_SUB_ITEMS = [
  { label: "Resumen", path: "/accounting", requiredCapability: "finance.read" },
  { label: "Ingresos", path: "/accounting/income", requiredCapability: "finance.read" },
  { label: "Regularización fiscal histórica", path: "/accounting/legacy-tax-reconciliation", requiredCapability: "finance.read" },
  { label: "Gastos", path: "/accounting/expenses", requiredCapability: "finance.read" },
  { label: "Tesorería", path: "/accounting/treasury", requiredCapability: "finance.write" },
  { label: "Proyectos y margen", path: "/accounting/projects-margin", requiredCapability: "finance.write" },
  { label: "Presupuestos", path: "/accounting/budgets", requiredCapability: "finance.read" },
  { label: "Contabilidad y conciliación", path: "/accounting/ledger", requiredCapability: "finance.configure" },
  { label: "Importaciones", path: "/accounting/imports", requiredCapability: "finance.import" },
  { label: "Gerencia", path: "/accounting/management", requiredCapability: "finance.read", adminOnly: true },
  { label: "Configuración financiera", path: "/accounting/settings", requiredCapability: "finance.configure" },
] as const;

/** Returns true when `location` is under the /accounting subtree. */
export function isFinanceRouteActive(location: string): boolean {
  if (location === "/accounting/suppliers" || location.startsWith("/accounting/suppliers/")) return false;
  return location === "/accounting" || location.startsWith("/accounting/");
}

/** Returns true when `role` has access to the Finance nav section. */
export function hasFinanceNavAccess(role: string): boolean {
  return hasCapability(role as FinanceRole, "finance.read");
}

export function hasFinanceSubItemAccess(item: FinanceSubItem, role: string): boolean {
  if (item.adminOnly && role !== "admin") return false;
  return hasCapability(role as FinanceRole, item.requiredCapability);
}

export function getVisibleFinanceSubItems(role: string): readonly FinanceSubItem[] {
  return FINANCE_SUB_ITEMS.filter((item) => hasFinanceSubItemAccess(item, role));
}

/**
 * Returns true when the given finance sub-item path should be highlighted
 * at the given location, including legacy supplier-invoice routes under Gastos.
 *
 * /accounting        — active only for exact /accounting
 * /accounting/income — active for /accounting/income and /accounting/income/*
 * /accounting/expenses — active for /accounting/expenses, /accounting/expenses/*,
 *                        /accounting/supplier-invoices, /accounting/supplier-invoices/*
 */
export function isFinanceSubItemActive(itemPath: string, location: string): boolean {
  if (itemPath === "/accounting") {
    return location === "/accounting";
  }
  if (itemPath === "/accounting/income") {
    return location === "/accounting/income" || location.startsWith("/accounting/income/");
  }
  if (itemPath === "/accounting/expenses") {
    return (
      location === "/accounting/expenses" ||
      location.startsWith("/accounting/expenses/") ||
      location === "/accounting/supplier-invoices" ||
      location.startsWith("/accounting/supplier-invoices/")
    );
  }
  return location === itemPath || location.startsWith(`${itemPath}/`);
}

function financeItemForLocation(location: string): FinanceSubItem | null {
  if (location === "/accounting/suppliers" || location.startsWith("/accounting/suppliers/")) {
    return { label: "Proveedores", path: "/accounting/suppliers", requiredCapability: "finance.read" };
  }
  if (
    location === "/accounting/supplier-invoices" ||
    location.startsWith("/accounting/supplier-invoices/")
  ) {
    return FINANCE_SUB_ITEMS.find((item) => item.path === "/accounting/expenses") ?? null;
  }
  return FINANCE_SUB_ITEMS.find((item) => (
    item.path === "/accounting"
      ? location === "/accounting"
      : location === item.path || location.startsWith(`${item.path}/`)
  )) ?? null;
}

/**
 * Returns the display label for the active finance route, or null for non-finance routes.
 * Legacy supplier-invoice routes return "Gastos".
 */
export function getFinanceRouteLabel(location: string): string | null {
  return financeItemForLocation(location)?.label ?? null;
}

export function canAccessFinanceRoute(location: string, role: string): boolean {
  const item = financeItemForLocation(location);
  if (!item) return false;
  return hasFinanceSubItemAccess(item, role);
}
